<script setup>
import { ref, nextTick } from 'vue';

defineProps({
  chunks: {
    type: Array,
    default: () => [],
  },
});

const expanded = ref(false);
const highlightedIndex = ref(null);

const toggle = () => {
  expanded.value = !expanded.value;
};

const truncate = content => {
  if (!content) return '';
  return content.length > 200 ? `${content.slice(0, 200)}…` : content;
};

// Called by SandboxBotMessage when an inline [n] citation marker is clicked.
const expandAndScrollTo = async chunkIndex => {
  expanded.value = true;
  highlightedIndex.value = chunkIndex;
  await nextTick();
  const el = document.getElementById(`citation-chunk-${chunkIndex}`);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
};

defineExpose({ expandAndScrollTo });
</script>

<template>
  <div v-if="chunks.length" class="mt-2 border-t border-slate-100 pt-2">
    <button
      type="button"
      class="p-0 text-xs font-medium text-woot-500 hover:underline"
      @click="toggle"
    >
      {{
        $t('ONBOARDING_SHELL.STEPS.SANDBOX.SOURCES_LABEL', {
          count: chunks.length,
        })
      }}
      <!-- Only chevron-down exists in the icon set — rotate it for the "up" state. -->
      <fluent-icon
        icon="chevron-down"
        size="12"
        :class="{ 'rotate-180': expanded }"
        class="inline-block transition-transform"
      />
    </button>
    <div v-if="expanded" class="mt-2 flex flex-col gap-2">
      <div
        v-for="chunk in chunks"
        :id="`citation-chunk-${chunk.chunk_index}`"
        :key="chunk.chunk_index"
        class="rounded-md border p-2 transition-colors"
        :class="
          highlightedIndex === chunk.chunk_index
            ? 'border-woot-500 bg-woot-25 dark:bg-woot-900/20'
            : 'border-slate-100 bg-slate-25 dark:bg-slate-800'
        "
      >
        <div class="flex items-center justify-between gap-2">
          <span class="text-xs font-medium text-slate-700 dark:text-slate-200">
            {{ chunk.document_name }}
          </span>
          <span
            v-if="chunk.score !== null && chunk.score !== undefined"
            class="text-xxs text-slate-400"
          >
            {{
              $t('ONBOARDING_SHELL.STEPS.SANDBOX.MATCH_SCORE', {
                score: Math.round(chunk.score * 100),
              })
            }}
          </span>
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
          {{ truncate(chunk.content) }}
        </p>
      </div>
    </div>
  </div>
</template>
